import React from 'react';
import { Plus, Minus, Home, Layers, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { VI_MESSAGES } from '@/constants/vietnamese';

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onHome: () => void;
  onBasemapToggle?: () => void;
  onLocation?: () => void;
  locationEnabled?: boolean;
  className?: string;
}

/**
 * Component điều khiển bản đồ
 */
export const MapControls: React.FC<MapControlsProps> = ({
  onZoomIn,
  onZoomOut,
  onHome,
  onBasemapToggle,
  onLocation,
  locationEnabled = false,
  className = '',
}) => {
  return (
    <TooltipProvider>
      <div className={`flex flex-col gap-2 ${className}`}>
        {/* Zoom in */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8 bg-white/90 backdrop-blur-sm shadow-md hover:bg-white/95 hover:shadow-lg"
              onClick={onZoomIn}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>{VI_MESSAGES.MAP.ZOOM_IN}</p>
          </TooltipContent>
        </Tooltip>

        {/* Zoom out */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8 bg-white/90 backdrop-blur-sm shadow-md hover:bg-white/95 hover:shadow-lg"
              onClick={onZoomOut}
            >
              <Minus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>{VI_MESSAGES.MAP.ZOOM_OUT}</p>
          </TooltipContent>
        </Tooltip>

        {/* Home */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8 bg-white/90 backdrop-blur-sm shadow-md hover:bg-white/95 hover:shadow-lg"
              onClick={onHome}
            >
              <Home className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>{VI_MESSAGES.MAP.HOME}</p>
          </TooltipContent>
        </Tooltip>

        {/* Basemap toggle */}
        {onBasemapToggle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8 bg-white/90 backdrop-blur-sm shadow-md hover:bg-white/95 hover:shadow-lg"
                onClick={onBasemapToggle}
              >
                <Layers className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{VI_MESSAGES.MAP.BASEMAP}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Location button */}
        {onLocation && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant={locationEnabled ? "default" : "secondary"}
                className={`h-8 w-8 ${locationEnabled ? 'shadow-md hover:shadow-lg' : 'bg-white/90 backdrop-blur-sm shadow-md hover:bg-white/95 hover:shadow-lg'}`}
                onClick={onLocation}
              >
                <Navigation className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{locationEnabled ? VI_MESSAGES.MAP.LOCATION_ON : VI_MESSAGES.MAP.LOCATION}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};
